import { CompanyParametersQuery, ContextKeys, CurrencyCollectionQuery, CurrencyItem, DocumentCalculatorBoPart, DocumentCalculatorModelPart, VatRateForPurchaseQuery, VatRateItem, convertItemToBaseCurrency, propertyChangedFilter, propertyChangedJoinPointFilter } from '@skylab-shared/business';
import { BusinessObjectPartContext, PropertyChangedJoinPoint } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, InventoryCostCalculationMethod, PurchaseDocumentKind, PurchaseInvoiceDocumentItemModel, VatSummary, VatTableItem, VatTableItemModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding, sameValues } from '@skylab-shared/utils';
import { CalculatedDocument, DocumentAdjustmentCalculator, DocumentAdjustmentGrossCalculator, DocumentAdjustmentNetCalculator, DocumentCalculator, DocumentGrossCalculator, DocumentNetCalculator, summariseDocument } from '@skylab/calculations/document';
import { DocumentItemChangesProperties } from '@skylab/calculations/document-item';

export interface PurchaseDocumentCalculatorModelPart extends DocumentCalculatorModelPart<PurchaseInvoiceDocumentItemModel> {
    kind: PurchaseDocumentKind;
    costCalculationMethod: InventoryCostCalculationMethod;
    vatSummary?: VatSummary;
    automaticVatCalculation: boolean;
    currencyGrossValue: number | null;
}

const PurchaseCalculatorsMap = {
    [PurchaseDocumentKind.PurchaseInvoice]: {
        [CalculationMethod.Net]: DocumentNetCalculator,
        [CalculationMethod.Gross]: DocumentGrossCalculator
    },
    [PurchaseDocumentKind.PurchaseInvoiceAdjustment]: {
        [CalculationMethod.Net]: DocumentAdjustmentNetCalculator,
        [CalculationMethod.Gross]: DocumentAdjustmentGrossCalculator
    }
} as const;

export class PurchaseDocumentCalculatorBoPart extends DocumentCalculatorBoPart<PurchaseDocumentCalculatorModelPart, PurchaseInvoiceDocumentItemModel> {
    public override async initializeAsync(context: BusinessObjectPartContext<PurchaseDocumentCalculatorModelPart>): Promise<void> {
        await super.initializeAsync(context);
        await this.bo.localContext.preload(VatRateForPurchaseQuery);

        this.registerCostRecalculationAfterExchangeRateChange();
        this.registerManuallyDocumentRecalculation();
        this.registerManuallyDocumentRecalculationDisabled();
    }

    private registerCostRecalculationAfterExchangeRateChange(): void {
        this.coreFeature.propertyChanged.listen<ExchangeRateModel>(propertyChangedFilter(
            ExchangeRateModel,
            ['rate', 'units'],
            _ => {
                this.bo.model.items.forEach(elem => {
                    this.recalculateAndRewrite_InventoryCost(elem, this.bo.model.exchangeRate);
                });
            }
        ));
    }

    protected override recalculateTItemModelAndRewrite(itemModel: PurchaseInvoiceDocumentItemModel, exchangeRate: ExchangeRateModel, updatedProperty?: DocumentItemChangesProperties): void {
        super.recalculateTItemModelAndRewrite(itemModel, exchangeRate, updatedProperty);
        this.recalculateAndRewrite_InventoryCost(itemModel, exchangeRate);
    }

    private recalculateAndRewrite_InventoryCost(itemModel: PurchaseInvoiceDocumentItemModel, exchangeRate: ExchangeRateModel) {
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId });
        const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery);
        const inForeignCurrency = this.bo.model.currencyId !== this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;

        const isGross = this.bo.model.costCalculationMethod === InventoryCostCalculationMethod.Gross;
        let cost = isGross ? itemModel.value_grossAfterDiscount : itemModel.value_netAfterDiscount;
        let price = isGross ? itemModel.price_grossAfterDiscount : itemModel.price_netAfterDiscount;

        if (inForeignCurrency && exchangeRate) {
            const rate = (exchangeRate.rate || 1) / (exchangeRate.units || 1);
            cost = Calc.round(cost * rate, precision, MidpointRounding.AwayFromZero);
            price = Calc.round(price * rate, precision, MidpointRounding.AwayFromZero);
        }

        itemModel.inventoryTotalCost = cost;
        itemModel.inventoryUnitCost = price;
    }

    public override get documentPropertiesThatTriggerRecalculation(): (keyof PurchaseDocumentCalculatorModelPart)[] {
        return [
            ...super.documentPropertiesThatTriggerRecalculation,
            'costCalculationMethod',
            'automaticVatCalculation'
        ];
    }

    protected recalculateDocumentModel(documentModel: PurchaseDocumentCalculatorModelPart): Readonly<{ inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }> {
        const result = { inDocumentCurrency: null, inBaseCurrency: null };

        const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery);
        const inForeignCurrency = documentModel.currencyId !== this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: documentModel.currencyId });

        const calculationContext = { precision };
        const calculator = new PurchaseCalculatorsMap[documentModel.kind as keyof typeof PurchaseCalculatorsMap][documentModel.calculationMethod as CalculationMethod.Net | CalculationMethod.Gross]();

        const { precision: systemCurrencyPrecision } = this.bo.localContext.getPreloaded<CurrencyItem>(ContextKeys.systemCurrency);

        const inBaseCurrencyItems = documentModel.items
            .map(item => convertItemToBaseCurrency(
                this.convertDocumentItemToItem(item, {
                    rate: this.getVatRateItem(item.vatRateId).rate,
                    exchangeRate: { rate: 1, units: 1 },
                    calculationMethod: documentModel.calculationMethod,
                    precision
                }),
                inForeignCurrency && (documentModel.exchangeRate !== null) ? {
                    rate: documentModel.exchangeRate.rate,
                    units: documentModel.exchangeRate.units
                } : { rate: 1, units: 1 },
                systemCurrencyPrecision
            ));

        if (calculator instanceof DocumentCalculator) {
            result.inBaseCurrency = calculator.recalculate(inBaseCurrencyItems as any, calculationContext) as any;
        } else if (calculator instanceof DocumentAdjustmentCalculator) {
            result.inBaseCurrency = calculator.recalculate(
                inBaseCurrencyItems,
                documentModel.vatSummary!.vatTable.items,
                calculationContext
            ) as any;
        }

        if (inForeignCurrency) {
            const items = documentModel.items
                .map(documentItem => {
                    const { rate } = this.getVatRateItem(documentItem.vatRateId);
                    return this.convertDocumentItemToItem(documentItem, {
                        rate,
                        exchangeRate: inForeignCurrency && (documentModel.exchangeRate !== null) ? {
                            rate: documentModel.exchangeRate.rate,
                            units: documentModel.exchangeRate.units
                        } : { rate: 1, units: 1 },
                        calculationMethod: documentModel.calculationMethod,
                        precision
                    });
                });

            const calculationContext = { precision };
            const calculator = new PurchaseCalculatorsMap[documentModel.kind as keyof typeof PurchaseCalculatorsMap][documentModel.calculationMethod as CalculationMethod.Net | CalculationMethod.Gross]();
            if (calculator instanceof DocumentCalculator) {
                this.bo.model.currencyGrossValue = calculator.recalculate(items as any, calculationContext).grossValue;
            } else if (calculator instanceof DocumentAdjustmentCalculator) {
                this.bo.model.currencyGrossValue = calculator.recalculate(
                    items,
                    inForeignCurrency && (documentModel.exchangeRate !== null) ? documentModel.vatSummary!.currencyVatTable!.items : documentModel.vatSummary!.vatTable.items,
                    calculationContext
                ).grossValue;
            }
        } else {
            this.bo.model.currencyGrossValue = null;
        }

        return result as any;
    }

    protected getVatRateItem(id: number): VatRateItem {
        return this.bo.localContext.firstPreloaded(VatRateForPurchaseQuery, { id });
    }

    protected override rewriteToDocumentModel(documentModel: PurchaseDocumentCalculatorModelPart, document: { inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }): void {
        if (!this.bo.model.automaticVatCalculation) return;

        documentModel.vatTableItems.clear();

        document.inBaseCurrency.vatTableItems.map(item => {
            const model = new VatTableItemModel();
            documentModel.vatTableItems.push(model);
            this.rewriteVatTableItemToVatTableItemModel(item, model);
        });

        documentModel.netValue = document.inBaseCurrency.netValue;
        documentModel.grossValue = document.inBaseCurrency.grossValue;
    }

    private registerManuallyDocumentRecalculation(): void {
        const recalculate = () => {
            if (this.bo.model.calculationMethod === CalculationMethod.Manual) return;
            if (this.bo.model.automaticVatCalculation) return;

            const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery);
            const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
            const convertItem = (item: VatTableItemModel): VatTableItem => ({grossValue: item.grossValue, netValue: item.netValue, vatRate: this.bo.localContext.firstPreloaded(VatRateForPurchaseQuery, { id: item.vatRateId }).rate, vatRateId: item.vatRateId, vatValue: item.vatValue});

            const result = summariseDocument(this.bo.model.vatTableItems.map(convertItem), precision);

            this.bo.model.netValue = result.netValue;
            this.bo.model.grossValue = result.grossValue;
        };

        this.registerListening(this.bo.model.vatTableItems.onCollectionChanged(() => {
            recalculate();
        }));

        this.coreFeature.propertyChanged.listen<VatTableItemModel>(propertyChangedFilter<VatTableItemModel>(
            VatTableItemModel,
            ['grossValue', 'vatValue', 'netValue'],
            _ => {
                recalculate();
            }
        ));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['calculationMethod'])(
            ctx => {
                if ((ctx.oldValue !== CalculationMethod.Manual) && (this.bo.model.calculationMethod === CalculationMethod.Manual)) {
                    this.bo.model.automaticVatCalculation = false;
                } else if ((ctx.oldValue === CalculationMethod.Manual) && (this.bo.model.calculationMethod !== CalculationMethod.Manual)) {
                    this.bo.model.automaticVatCalculation = true;
                }
            }
        )));
    }

    private registerManuallyDocumentRecalculationDisabled(): void {
        const handle = () => {
            if (this.bo.model.automaticVatCalculation) return;

            const vatTableVatRates = new Set(this.bo.model.vatTableItems.map(elem => elem.vatRateId));
            const itemsVatTableRates = new Set(this.bo.model.items.map(elem => elem.vatRateId));

            const sameVatRates = (vatTableVatRates.size === itemsVatTableRates.size) && sameValues([...vatTableVatRates], [...itemsVatTableRates]);

            if (!sameVatRates) {
                this.bo.model.automaticVatCalculation = true;
            }
        };

        this.registerListening(this.bo.model.items.onCollectionChanged(ctx => {
            handle();
        }));

        this.coreFeature.propertyChanged.listen<PurchaseInvoiceDocumentItemModel>(propertyChangedFilter<PurchaseInvoiceDocumentItemModel>(
            PurchaseInvoiceDocumentItemModel,
            ['vatRateId'],
            _ => {
                handle();
            }
        ));
    }
}
